import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Award, BarChart3, Bitcoin, BookOpen, Calculator, CalendarDays, CandlestickChart, ChevronDown, Clock3, FileClock, Flame, Gift, Globe, GraduationCap, History, Home as HomeI, Languages, LifeBuoy, LogOut, Medal, Menu, MonitorSmartphone, Moon, NotebookPen, Percent, PlusCircle, Repeat, ScrollText, ShieldCheck, Sun, Trophy, UserRound, Users, Wallet, Zap } from 'lucide-react';
import { Avatar, Logo, switchTheme, ThemeToggle, UpdateNotice, type ReleaseNote } from '@ck/ui';
import { LANGS, usePrefs } from './prefs';
import type { PublicTenant, Trader } from '@ck/sdk';
import { api, remember, staffView, API_URL } from './api';
import { PortalCtx, type Portal } from './state';
import { Home } from './pages/Home';
import { SupportWatch } from './SupportWatch';
import { OfferPopup } from './OfferPopup';
import { CouponNotice } from './CouponNotice';

/** Pages load on demand so the first screen is small and fast; they are prefetched right after start-up. */
function lazyPage(load: () => Promise<Record<string, unknown>>, name: string): ComponentType {
  const C = lazy(() => load().then((m) => ({ default: m[name] as ComponentType })));
  return function Page() { return <Suspense fallback={<div className="page-load" aria-busy="true"><i /><i /><i /></div>}><C /></Suspense>; };
}
const L_Badges = () => import('./pages/Badges');
const Badges = lazyPage(L_Badges, 'Badges');
const L_Buy = () => import('./pages/Buy');
const Buy = lazyPage(L_Buy, 'Buy');
const L_Money = () => import('./pages/Money');
const Points = lazyPage(L_Money, 'Points');
const Withdrawal = lazyPage(L_Money, 'Withdrawal');
const Orders = lazyPage(L_Money, 'Orders');
const L_Community = () => import('./pages/Community');
const Leaderboard = lazyPage(L_Community, 'Leaderboard');
const Offers = lazyPage(L_Community, 'Offers');
const Transparency = lazyPage(L_Community, 'Transparency');
const L_Resources = () => import('./pages/Resources');
const CopyTrading = lazyPage(L_Resources, 'CopyTrading');
const Backtesting = lazyPage(L_Resources, 'Backtesting');
const Platforms = lazyPage(L_Resources, 'Platforms');
const University = lazyPage(L_Resources, 'University');
const RulesFaq = lazyPage(L_Resources, 'RulesFaq');
const L_Performance = () => import('./pages/Performance');
const Stats = lazyPage(L_Performance, 'Stats');
const Notebook = lazyPage(L_Performance, 'Notebook');
const TrackRecord = lazyPage(L_Performance, 'TrackRecord');
const Certificates = lazyPage(L_Performance, 'Certificates');
const L_ToolsPages = () => import('./pages/ToolsPages');
const Tools = lazyPage(L_ToolsPages, 'Tools');
const Calendar = lazyPage(L_ToolsPages, 'Calendar');
const Utilities = lazyPage(L_ToolsPages, 'Utilities');
const FuturesDesk = lazyPage(L_ToolsPages, 'FuturesDesk');
const PerpsDesk = lazyPage(L_ToolsPages, 'PerpsDesk');
const L_AccountPages = () => import('./pages/AccountPages');
const Affiliate = lazyPage(L_AccountPages, 'Affiliate');
const Profile = lazyPage(L_AccountPages, 'Profile');
const LiveMarket = lazyPage(L_AccountPages, 'LiveMarket');
const L_Support = () => import('./pages/Support');
const Support = lazyPage(L_Support, 'Support');
if (typeof window !== 'undefined') {
  const warm = () => [L_Buy, L_Money, L_Community, L_Resources, L_Performance, L_ToolsPages, L_AccountPages, L_Support, L_Badges].forEach((f) => { void f().catch(() => undefined); });
  const ric = (window as unknown as { requestIdleCallback?: (f: () => void) => void }).requestIdleCallback;
  setTimeout(() => (ric ? ric(warm) : warm()), 1500);
}


type Item = { id: string; label: string; icon: ReactNode; badge?: string; c: ComponentType };

const MENU: { group: string; items: Item[] }[] = [
  { group: 'Main menu', items: [
    { id: 'home', label: 'Home', icon: <HomeI />, c: Home },
    { id: 'points', label: 'CK Points', icon: <Gift />, c: Points },
    { id: 'withdrawal', label: 'Withdrawal', icon: <Wallet />, c: Withdrawal },
    { id: 'leaderboard', label: 'Leaderboard', icon: <Trophy />, c: Leaderboard },
    { id: 'buy', label: 'Buy New Challenge', icon: <PlusCircle />, c: Buy },
    { id: 'offers', label: 'My Offers', icon: <Percent />, c: Offers },
    { id: 'transparency', label: 'Transparency', icon: <ShieldCheck />, badge: 'New', c: Transparency },
    { id: 'orders', label: 'Order History', icon: <History />, c: Orders },
    { id: 'copy', label: 'Copy trading', icon: <Repeat />, c: CopyTrading },
    { id: 'platforms', label: 'Trading Platforms', icon: <MonitorSmartphone />, c: Platforms },
    { id: 'affiliate', label: 'Affiliate', icon: <Users />, c: Affiliate },
  ] },
  { group: 'Back Testing', items: [{ id: 'backtesting', label: 'Back Testing', icon: <FileClock />, c: Backtesting }] },
  { group: 'Performance', items: [
    { id: 'stats', label: 'My Stats', icon: <BarChart3 />, c: Stats },
    { id: 'record', label: 'Track Record', icon: <ScrollText />, c: TrackRecord },
    { id: 'notebook', label: 'Notebook', icon: <NotebookPen />, c: Notebook },
    { id: 'certificates', label: 'Certificates', icon: <Award />, c: Certificates },
    { id: 'badges', label: 'Badges', icon: <Medal />, c: Badges },
  ] },
  { group: 'Resources', items: [
    { id: 'university', label: 'University', icon: <GraduationCap />, c: University },
    { id: 'rules', label: 'Rules & FAQ', icon: <BookOpen />, c: RulesFaq },
  ] },
  { group: 'Tools', items: [
    { id: 'tools', label: 'All Tools', icon: <Calculator />, c: Tools },
    { id: 'calendar', label: 'Economic Calendar', icon: <CalendarDays />, c: Calendar },
  ] },
  { group: 'Utilities', items: [{ id: 'utilities', label: 'Utilities', icon: <Clock3 />, c: Utilities }] },
  { group: 'Help', items: [{ id: 'support', label: 'Support', icon: <LifeBuoy />, c: Support }] },
];
/** Each market has its own menu: extra desks for futures and perps, a short menu for markets that are not open yet. */
const MARKET_MENU: Record<string, (menu: typeof MENU) => typeof MENU> = {
  futures: (m) => [m[0]!, { group: 'Futures', items: [{ id: 'futures-desk', label: 'Futures desk', icon: <CandlestickChart />, c: FuturesDesk }] }, ...m.slice(1)],
  perps: (m) => [m[0]!, { group: 'Perps', items: [{ id: 'perps-desk', label: 'Perps desk', icon: <Bitcoin />, c: PerpsDesk }] }, ...m.slice(1).map((g) => ({ ...g, items: g.items.filter((i) => i.id !== 'calendar') }))],
  predict: (m) => [
    { group: 'CK Predict', items: [HOME, ...m[0]!.items.filter((i) => ['leaderboard', 'points', 'offers'].includes(i.id))] },
    { group: 'Learn', items: m.flatMap((g) => g.items).filter((i) => ['university', 'rules'].includes(i.id)) },
    m[m.length - 1]!,
  ],
  fx: (m) => [{ group: 'CK Market', items: [HOME] }, m[m.length - 1]!],
};
const HOME: Item = { id: 'home', label: 'Home', icon: <HomeI />, c: Home };
const menuFor = (brand: string) => (MARKET_MENU[brand] ?? ((m) => m))(MENU);
const ALL = [...MENU.flatMap((g) => g.items), { id: 'futures-desk', label: 'Futures desk', icon: null, c: FuturesDesk }, { id: 'perps-desk', label: 'Perps desk', icon: null, c: PerpsDesk }] as Item[];

function parseHash(): { market?: string; page?: string } {
  const [, market, page] = location.hash.split('/');
  return { market, page };
}

export function Shell({ tenant: fullTenant, trader, refreshTrader, signOut }: { tenant: PublicTenant; trader: Trader; refreshTrader: () => Promise<void>; signOut: () => void }) {
  // Markets and pages hidden for this client (country rules or a staff restriction) are left out entirely.
  const hiddenM = trader.access?.hiddenMarkets ?? [];
  const hiddenF = trader.access?.hiddenFeatures ?? [];
  const tenant = useMemo(() => ({ ...fullTenant, markets: fullTenant.markets.filter((m) => !hiddenM.includes(m.brand)) }), [fullTenant, hiddenM.join()]);
  const first = () => {
    const h = parseHash();
    const m = [h.market, remember('ck.market')].find((b) => b && tenant.markets.some((x) => x.brand === b)) ?? tenant.markets[0]!.brand;
    const p = h.page && ((ALL.some((i) => i.id === h.page) && !(trader.access?.hiddenFeatures ?? []).includes(h.page)) || h.page === 'profile') ? h.page : 'home';
    return { m, p };
  };
  const [{ m: brand, p: page }, setRoute] = useState(first);
  const menuGroups = useMemo(() => menuFor(brand).map((g) => ({ ...g, items: g.items.filter((i) => !hiddenF.includes(i.id)) })).filter((g) => g.items.length), [hiddenF.join(), brand]);
  // A page that the newly chosen market doesn't have falls back to its home.
  useEffect(() => { if (page !== 'profile' && page !== 'support' && !menuGroups.some((g) => g.items.some((i) => i.id === page))) setRoute({ m: brand, p: 'home' }); }, [brand]);
  const [menu, setMenu] = useState(false);
  const [userMenu, setUserMenu] = useState(false);

  // Each page change adds a history entry, so the browser's Back button returns to the previous page.
  const firstNav = useRef(true);
  useEffect(() => {
    remember('ck.market', brand);
    const want = `#/${brand}/${page}`;
    if (location.hash !== want) {
      if (firstNav.current) history.replaceState(null, '', want);
      else history.pushState(null, '', want);
    }
    firstNav.current = false;
    window.scrollTo(0, 0);
    setMenu(false);
  }, [brand, page]);
  useEffect(() => {
    const onHash = () => setRoute(first());
    window.addEventListener('hashchange', onHash);
    window.addEventListener('popstate', onHash);
    return () => { window.removeEventListener('hashchange', onHash); window.removeEventListener('popstate', onHash); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const market = tenant.markets.find((m) => m.brand === brand)!;
  const live = market.kind === 'live_account';
  const ctx: Portal = useMemo(() => ({
    tenant, trader, market, page,
    go: (p, m) => setRoute({ p, m: m ?? brand }),
    refreshTrader, signOut,
  }), [tenant, trader, market, page, brand, refreshTrader, signOut]);

  const Page = page === 'profile' ? Profile : live ? LiveMarket : (ALL.find((i) => i.id === page)?.c ?? Home);
  const b = tenant.branding;

  const { t, theme, setTheme, lang, setLang } = usePrefs();
  const menuRef = useRef<HTMLDivElement>(null);
  const [langOpen, setLangOpen] = useState(false);
  const [mini, setMini] = useState(() => { try { return localStorage.getItem('ck.mini') === '1'; } catch { return false; } });
  const toggleMini = () => { setMini((m) => { try { localStorage.setItem('ck.mini', m ? '0' : '1'); } catch { /* ok */ } return !m; }); };
  useEffect(() => {
    if (!userMenu) return;
    const close = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setUserMenu(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [userMenu]);
  const photo = trader.avatarUrl ? `${API_URL.replace(/\/$/, '')}${trader.avatarUrl}` : null;

  return (
    <PortalCtx.Provider value={ctx}>
      <div className={`shell${menu ? ' menu-open' : ''}${live ? ' no-side' : ''}${mini ? ' side-mini' : ''}`} style={{ ['--mk' as string]: market.accent }}>
        <UpdateNotice latest={() => api.get<{ release: ReleaseNote | null }>('/v1/releases/latest').then((r) => r.release)} />
        {!staffView() && <CouponNotice buy={() => setRoute({ m: brand, p: 'buy' })} />}
        {!staffView() && <OfferPopup buy={() => setRoute({ m: tenant.markets.find((x) => x.kind === 'challenge' && x.status === 'open')?.brand ?? brand, p: 'buy' })} />}
        {page !== 'support' && <SupportWatch open={(id) => { try { sessionStorage.setItem('ck.support.open', id); } catch { /* ignore */ } setRoute({ m: brand, p: 'support' }); }} />}
        {!live && (
          <aside className="side" aria-label="Client menu">
            <a className="side-brand" href="https://ckfundedpro.com" aria-label="CK Funded Pro website">
              <Logo branding={b} on="dark" height={26} />
            </a>
            <SideAccounts brand={brand} onAll={() => { setMenu(false); try { sessionStorage.removeItem('ck.dash'); } catch { /* ok */ } window.dispatchEvent(new Event('ck:home')); setRoute({ m: brand, p: 'home' }); window.scrollTo(0, 0); }} onPick={(id) => { setMenu(false); try { sessionStorage.setItem('ck.dash', id); } catch { /* ok */ } setRoute({ m: brand, p: 'home' }); window.dispatchEvent(new CustomEvent('ck:open-account', { detail: id })); }} onBuy={() => { setMenu(false); setRoute({ m: brand, p: 'buy' }); }} />
            <nav className="side-scroll">
              {menuGroups.map((g) => (
                <div key={g.group} className="side-g">
                  {g.items.length > 1 && <span className="side-h">{t(g.group)}</span>}
                  {g.items.map((it) => (
                    <a key={it.id} href={`#/${brand}/${it.id}`} title={t(it.label)} className={`side-a${page === it.id ? ' on' : ''}`} aria-current={page === it.id ? 'page' : undefined}
                      onClick={(e) => { e.preventDefault(); setMenu(false); if (it.id === 'home') window.dispatchEvent(new Event('ck:home')); setRoute({ m: brand, p: it.id }); }}>
                      <span className="ic" aria-hidden="true">{it.icon}</span><span className="side-lb">{t(it.label)}</span>{it.badge && <span className="new">{t(it.badge)}</span>}
                    </a>
                  ))}
                </div>
              ))}
            </nav>
            <div className="side-foot">
              {trader.kycStatus !== 'approved' && <button className="side-warn" onClick={() => setRoute({ m: brand, p: 'profile' })}><ShieldCheck /><span><b style={{ display: 'block', color: '#F2B705', fontSize: 14 }}>{t('Verification needed')}</b>Unlock payouts and CK Trader</span></button>}
              <div className="side-me notranslate"><Avatar src={photo} name={`${trader.firstName} ${trader.lastName}`} size={36} /><div><b>{trader.firstName} {trader.lastName}</b><small title={trader.email}>{trader.kycStatus === 'approved' ? t('Verified trader') : trader.email.toLowerCase()}</small></div></div>
              <button className="side-out" onClick={signOut}><LogOut />{t('Sign out')}</button>
            </div>
          </aside>
        )}
        {menu && <div className="scrim" onClick={() => setMenu(false)} />}

        <header className="top">
          <div className="top-in">
            {!live && <button className="burger" aria-label="Open menu" aria-expanded={menu} onClick={() => setMenu(!menu)}><Menu /></button>}
            {!live && <button className="mini-t" aria-label={mini ? 'Expand menu' : 'Collapse menu'} title={mini ? 'Expand menu' : 'Collapse menu'} aria-pressed={mini} onClick={toggleMini}><Menu /></button>}
            <a className="brand" href="https://ckfundedpro.com" aria-label="CK Funded Pro website"><Logo branding={b} compact height={30} on={theme === 'dark' ? 'dark' : 'light'} /></a>
            <nav className="switch" aria-label="Markets" role="tablist">
              {tenant.markets.map((m) => (
                <button key={m.brand} role="tab" aria-selected={m.brand === brand} className={`sw${m.brand === brand ? ' on' : ''}`}
                  style={{ ['--acc' as string]: m.accent }}
                  onClick={() => setRoute({ m: m.brand, p: m.kind === 'live_account' ? 'home' : page === 'profile' ? 'home' : page })}>
                  <span className="sw-logo" aria-hidden="true"><Logo branding={b} compact on="dark" height={20} /></span>{m.name}{m.status === 'coming_soon' && <span className="sw-soon">Soon</span>}
                </button>
              ))}
            </nav>
            <span className="spacer" />
            <ThemeToggle theme={theme} onChange={setTheme} labels={{ light: t('Light mode'), dark: t('Dark mode') }} />
            <div className="user" ref={menuRef}>
              <button className="avatar" aria-haspopup="menu" aria-expanded={userMenu} onClick={() => setUserMenu(!userMenu)}>
                <Avatar src={photo} name={`${trader.firstName} ${trader.lastName}`} size={28} /><span className="nm">{trader.firstName}</span><ChevronDown />
              </button>
              {userMenu && (
                <div className="user-menu" role="menu">
                  <div className="um-h"><b>{trader.firstName} {trader.lastName}</b><small>{trader.email}</small></div>
                  <button role="menuitem" onClick={() => { setUserMenu(false); setRoute({ m: brand, p: 'profile' }); }}><UserRound />{t('Profile & security')}</button>
                  <button role="menuitem" onClick={() => { setUserMenu(false); switchTheme(theme === 'dark' ? 'light' : 'dark', setTheme); }}>{theme === 'dark' ? <Sun /> : <Moon />}{theme === 'dark' ? t('Light mode') : t('Dark mode')}</button>
                  <button role="menuitem" aria-expanded={langOpen} onClick={() => setLangOpen(!langOpen)}><Languages />{t('Language')}<span className="um-lang">{LANGS.find((l) => l[0] === lang)?.[2]} {langOpen ? '▴' : '▾'}</span></button>
                  {langOpen && <div className="um-langs" role="group" aria-label={t('Language')}>{LANGS.map(([k, name, code]) => <button key={k} role="menuitemradio" aria-checked={lang === k} lang={k} className={lang === k ? 'on' : ''} onClick={() => { setLang(k); setLangOpen(false); }}><span>{name}</span><small>{code}</small>{lang === k && <b aria-hidden="true">✓</b>}</button>)}</div>}
                  <button role="menuitem" onClick={() => { setUserMenu(false); setRoute({ m: brand, p: 'support' }); }}><LifeBuoy />{t('Contact support')}</button>
                  <hr />
                  <button role="menuitem" className="danger" onClick={signOut}><LogOut />{t('Sign out')}</button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="main" id="main">
          {!live && <OfferTicker />}
          <div className="page-in" key={`${brand}:${page}`}><Page /></div>
          <footer className="foot">
            <span>{tenant.legalName}</span>
            <span>Evaluations run in simulated environments. Rewards follow programme terms. Trading carries risk. 18+ only.</span>
          </footer>
        </main>
      </div>
    </PortalCtx.Provider>
  );
}

/** The streaming offers strip at the top of every page. */
function OfferTicker() {
  const [items, setItems] = useState<{ t: string; code?: string }[]>([]);
  useEffect(() => {
    api.get<{ offers: { code: string; title: string; percent_off: number }[] }>('/v1/me/offers').then((r) => setItems(r.offers.map((o) => ({ t: `${o.title} · ${o.percent_off}% OFF`, code: o.code })))).catch(() => undefined);
  }, []);
  const base: { t: string; ic: ReactNode }[] = [
    { t: 'Fund up to $200,000 and keep up to 90% of the profits', ic: <Trophy /> },
    { t: 'Pass once, then climb five funding stages', ic: <Zap /> },
    { t: 'CK Trader, our in-house platform, on every device', ic: <MonitorSmartphone /> },
    { t: 'Support 24/7 in English, Kiswahili, Español, Português, 中文, 한국어 and Français', ic: <Globe /> },
  ];
  const all: { t: string; ic: ReactNode; code?: string; hot?: boolean }[] = [...items.map((x) => ({ ...x, ic: <Flame />, hot: true })), ...base];
  return (
    <div className="tick" aria-label="Offers and news">
      <span className="tick-tag"><span className="tick-dot" />LIVE</span>
      <div className="tick-track"><div className="tick-in">{[...all, ...all].map((x, i) => <span key={i} className={x.hot ? 'hot' : ''}><b className="tick-ic" aria-hidden="true">{x.ic}</b>{x.t}{x.code && <em className="notranslate">{x.code}</em>}<i aria-hidden="true">✦</i></span>)}</div></div>
    </div>
  );
}

/** Account picker and New Challenge button at the top of the menu. */
function SideAccounts({ brand, onPick, onBuy, onAll }: { brand: string; onPick: (id: string) => void; onBuy: () => void; onAll: () => void }) {
  const [list, setList] = useState<{ id: string; login: string; createdAt: string }[]>([]);
  const [cur, setCur] = useState('');
  useEffect(() => {
    let on = true;
    const load = () => api.get<{ accounts: { account: { id: string; login: string; createdAt: string } }[] }>(`/v1/me/accounts?brand=${brand}`)
      .then((r) => { if (on) setList(r.accounts.map((a) => a.account).sort((a, b) => a.createdAt.localeCompare(b.createdAt))); }).catch(() => undefined);
    void load();
    const iv = setInterval(() => { if (!document.hidden) void load(); }, 20_000);
    window.addEventListener('focus', load);
    const f = (e: Event) => setCur(String((e as CustomEvent).detail ?? ''));
    const h = () => setCur('');
    window.addEventListener('ck:open-account', f); window.addEventListener('ck:home', h); window.addEventListener('ck:dash', f);
    return () => { on = false; clearInterval(iv); window.removeEventListener('focus', load); window.removeEventListener('ck:open-account', f); window.removeEventListener('ck:home', h); window.removeEventListener('ck:dash', f); };
  }, [brand]);
  return (
    <div className="side-acc">
      {<select className="side-pick" value={cur} aria-label="Choose an account" onChange={(e) => { setCur(e.target.value); if (e.target.value) onPick(e.target.value); else onAll(); }}>
        <option value="">{list.length ? `All accounts (${list.length})` : 'No accounts yet'}</option>
        {[...list].reverse().map((a) => <option key={a.id} value={a.id}>Account {list.indexOf(a) + 1} - {a.login}</option>)}
      </select>}
      <button className="side-new" onClick={onBuy}>New Challenge</button>
    </div>
  );
}
